import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface AnalyticsProps {
  selectedBrand: string | null
}

const Analytics: React.FC<AnalyticsProps> = ({ selectedBrand }) => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10), // 30 days ago
    endDate: new Date().toISOString().slice(0, 10), // today
  })

  const { data: analytics, isLoading } = trpc.contentManagement.getAnalytics.useQuery({
    brandId: selectedBrand || undefined,
    timeframe: "30d",
  })

  const { data: topContent } = trpc.contentManagement.getTopPerformingContent.useQuery({
    platform: "all",
    brandId: selectedBrand || undefined,
    timeframe: "30d",
    limit: 10,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Handle different response structures
  const stats = {
    totalPosts: (analytics as any)?.totalPosts || (analytics as any)?.overview?.totalPosts || 0,
    totalEngagement: (analytics as any)?.totalEngagement || 0,
    avgEngagementRate: (analytics as any)?.avgEngagementRate || (analytics as any)?.overview?.avgEngagement || 0,
    topPlatform: (analytics as any)?.topPlatform || "N/A",
    growthRate: (analytics as any)?.growthRate || 0,
    platformBreakdown: (analytics as any)?.platformBreakdown || {},
    engagementTrend: (analytics as any)?.engagementTrend || [],
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold ">Analytics</h2>
          <p className="">Track your content performance and engagement</p>
        </div>

        {/* Date Range Selector */}
        <div className="flex space-x-2">
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
          />
          <span className="self-center text-gray-500">to</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-background p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                <span className=" font-semibold">📊</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Posts</p>
              {/* @ts-ignore TODO: Fix analytics data structure */}
              <p className="text-2xl font-semibold ">{stats.totalPosts}</p>
            </div>
          </div>
        </div>

        <div className="bg-background p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                <span className="text-green-600 font-semibold">❤️</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Engagement</p>
              {/* @ts-ignore TODO: Fix analytics data structure */}
              <p className="text-2xl font-semibold ">{stats.totalEngagement.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-background p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                <span className="text-purple-600 font-semibold">📈</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg Engagement Rate</p>
              {/* @ts-ignore TODO: Fix analytics data structure */}
              <p className="text-2xl font-semibold ">{stats.avgEngagementRate.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-background p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                <span className="text-yellow-600 font-semibold">🏆</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Top Platform</p>
              {/* @ts-ignore TODO: Fix analytics data structure */}
              <p className="text-2xl font-semibold  capitalize">{stats.topPlatform}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform Breakdown */}
        <div className="bg-background p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium  mb-4">Platform Performance</h3>
          <div className="space-y-4">
            {/* @ts-ignore TODO: Fix analytics data structure */}
            {Object.entries(stats.platformBreakdown).map(([platform, data]: [string, any]) => (
              <div key={platform} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">
                    {platform === "linkedin" ? "💼" : platform === "twitter" ? "🐦" : "📱"}
                  </span>
                  <div>
                    <p className="font-medium  capitalize">{platform}</p>
                    <p className="text-sm text-gray-500">{data.posts} posts</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold ">{data.engagement.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">engagement</p>
                </div>
              </div>
            ))}

            {/* @ts-ignore TODO: Fix analytics data structure */}
            {Object.keys(stats.platformBreakdown).length === 0 && (
              <p className="text-gray-500 text-center py-4">No platform data available</p>
            )}
          </div>
        </div>

        {/* Top Performing Content */}
        <div className="bg-background p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium  mb-4">Top Performing Content</h3>
          <div className="space-y-4">
            {topContent?.slice(0, 5).map((item: any, index: number) => (
              <div key={item.content.id} className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-foregroundx rounded-full flex items-center justify-center text-xs font-medium ">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium  line-clamp-2">
                    {item.content.title || item.content.body.substring(0, 60) + "..."}
                  </p>
                  <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                    <span>{item.totalEngagement} engagement</span>
                    <span className="capitalize">{item.platform}</span>
                    <span>{new Date(item.content.publishedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}

            {(!topContent || topContent.length === 0) && (
              <p className="text-gray-500 text-center py-4">No content data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Engagement Trend Chart Placeholder */}
      <div className="bg-background p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium  mb-4">Engagement Trend</h3>
        {/* @ts-ignore TODO: Fix analytics data structure */}
        {stats.engagementTrend && stats.engagementTrend.length > 0 ? (
          <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <div className="text-4xl text-gray-400 mb-2">📈</div>
              <p className="text-gray-500">Chart visualization would go here</p>
              <p className="text-xs text-gray-400 mt-1">
                {/* @ts-ignore TODO: Fix analytics data structure */}
                {stats.engagementTrend.length} data points available
              </p>
            </div>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <div className="text-4xl text-gray-400 mb-2">📊</div>
              <p className="text-gray-500">No trend data available</p>
              <p className="text-xs text-gray-400 mt-1">Publish some content to see trends</p>
            </div>
          </div>
        )}
      </div>

      {/* Content Performance Insights */}
      <div className="bg-background p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium  mb-4">Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 mb-2">
              {/* @ts-ignore TODO: Fix analytics data structure */}
              {stats.growthRate > 0 ? "+" : ""}
              {stats.growthRate.toFixed(1)}%
            </div>
            <p className="text-sm ">Growth Rate</p>
            <p className="text-xs text-gray-500 mt-1">vs previous period</p>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold  mb-2">
              {/* @ts-ignore TODO: Fix analytics data structure */}
              {stats.avgEngagementRate.toFixed(1)}%
            </div>
            <p className="text-sm ">Avg Engagement Rate</p>
            <p className="text-xs text-gray-500 mt-1">across all platforms</p>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 mb-2">
              {/* @ts-ignore TODO: Fix analytics data structure */}
              {Math.round(stats.totalEngagement / Math.max(stats.totalPosts, 1))}
            </div>
            <p className="text-sm ">Avg Engagement per Post</p>
            <p className="text-xs text-gray-500 mt-1">likes, comments, shares</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-background p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium  mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-center">
            <div className="text-2xl mb-2">📊</div>
            <div className="text-sm font-medium ">Export Data</div>
          </button>
          <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-center">
            <div className="text-2xl mb-2">📧</div>
            <div className="text-sm font-medium ">Email Report</div>
          </button>
          <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-center">
            <div className="text-2xl mb-2">📈</div>
            <div className="text-sm font-medium ">View Trends</div>
          </button>
          <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-center">
            <div className="text-2xl mb-2">🎯</div>
            <div className="text-sm font-medium ">Set Goals</div>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Analytics
